import { http } from '@/api/http'
import type { Customer } from '@/lib/pos-types'
import { unwrapList, unwrapSingle } from '@/api/normalizers'

const normalizeCustomer = (raw: unknown): Customer => {
  const obj = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {}
  const get = (k: string) => obj[k]
  return {
    id: String(get('id') ?? ''),
    type: (String(get('type') ?? 'person') === 'company' ? 'company' : 'person'),
    name: String(get('name') ?? ''),
    taxId: (get('tax_id') ?? get('taxId')) ? String(get('tax_id') ?? get('taxId')) : undefined,
    email: obj.email ? String(obj.email) : undefined,
    phone: obj.phone ? String(obj.phone) : undefined,
    discountPercent: Number(obj.discount_percent ?? obj.discountPercent ?? 0) || 0,
    creditEnabled: Boolean(obj.credit_enabled ?? obj.creditEnabled ?? false),
    creditLimit: Number(obj.credit_limit ?? obj.creditLimit ?? 0) || 0,
    creditBalance: Number(obj.credit_balance ?? 0) || 0,
  }
}

export const customersApi = {
  list: async (): Promise<Customer[]> => {
    const res = await http.get<unknown>('/customers')
    return unwrapList(res.data).map(normalizeCustomer)
  },
  create: async (payload: {
    type: Customer['type']
    name: string
    taxId?: string | null
    email?: string | null
    phone?: string | null
    discountPercent?: number
    creditEnabled?: boolean
    creditLimit?: number
  }): Promise<Customer> => {
    const res = await http.post<unknown>('/customers', payload)
    return normalizeCustomer(unwrapSingle(res.data))
  },
  update: async (id: string, payload: Partial<{
    type: Customer['type']
    name: string
    taxId?: string | null
    email?: string | null
    phone?: string | null
    discountPercent?: number
    creditEnabled?: boolean
    creditLimit?: number
  }>): Promise<Customer> => {
    const res = await http.patch<unknown>(`/customers/${encodeURIComponent(id)}`, payload)
    return normalizeCustomer(unwrapSingle(res.data))
  },
  delete: async (id: string): Promise<void> => {
    await http.delete(`/customers/${encodeURIComponent(id)}`)
  },
}